import { useState, useEffect, useCallback, useMemo } from 'react';
import { userApi, usernameMarketApi } from '@/utils/api';
import { v2Api } from '@/utils/v2Api';
import { socket } from '@/utils/socket';
import { getCached, setCached, isCacheFresh, TTL, getAny } from '@/utils/cache';
import { useTranslation } from 'react-i18next';


import { BalanceBar } from '@/components/ui/BalanceBar';
import { BannerMediaLayer, BannerMediaControls } from '@/components/ui/BannerMedia';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { FilterBar } from '@/components/ui/FilterBar';
import { CardRenderer } from '@/components/Cards/CardRenderer';
import { LiveUsers } from '@/components/ui/LiveUsers';
import { DiscoveryStrip } from '@/components/ui/DiscoveryStrips';
import { FilterModal } from '@/components/Modals/FilterModal';
import { ExperimentalFilterBar } from '@/components/ui/ExperimentalFilterBar';
import { SectionListView } from '@/components/ui/SectionListView';
import { resolveListingCategory } from '@/utils/kindResolver';
import { FilterPanel, PricePanel } from '@/components/ui/FilterPanel';
import { CustomEmojiPlayer } from '@/components/ui/CustomEmojiPlayer';
import type { NamedFilter } from '@/components/ui/ExperimentalFilterBar';
import {
    Flame, Star, LayoutGrid, Clock, Activity,
    AtSign, Gem, Crown, Wallet, Repeat, ArrowUpDown, Store as StoreIcon, Users, ShoppingBag, Gavel, Handshake,
    UserRound, Archive, Send, CheckCircle2, XCircle, TimerReset, Filter as FilterIcon, Bot
} from 'lucide-react';

type StoreSection = 'all' | 'store' | 'offers' | 'auctions';

const HOURS_LEFT = (d: any) => {
    const end = d?.ends_at || d?.endTime;
    if (!end) return Infinity;
    const t = new Date(end).getTime();
    return (t - Date.now()) / 3_600_000;
};

// Mocks Removed

interface StoreProps {
    onNavigate: (page: string, category?: string) => void;
    onSearch?: () => void;
    isActive?: boolean;
}

const isBotHandle = (h?: string) => !!h && /bot$/i.test(String(h).replace(/^@/, '').trim());

const Store = ({ onNavigate, onSearch, isActive = true }: StoreProps) => {
    const { selectionChanged } = useHapticFeedback();
    const { t } = useTranslation('store');
    const { t: tc } = useTranslation('common');
    const [activeFilter, setActiveFilter] = useState('all');
    const [quickFind, setQuickFind] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'list' | 'compact' | 'activity'>('grid');
    // Data State (Initialize from persistent cache immediately)
    const [storeItems, setStoreItems] = useState<any[]>(() => getAny<any[]>('store_listings') || []);
    const [isProcessing, setIsProcessing] = useState(false);
    const [activeUsers, setActiveUsers] = useState<any[]>(() => getAny<{ users: any[] }>('active_users')?.users || []);
    const [totalActive, setTotalActive] = useState(() => getAny<{ totalActive: number }>('active_users')?.totalActive || 0);
    const [discovery, setDiscovery] = useState<{ brands: any[], skins: any[], cards: any[] }>(() => 
        getAny<{ discovery: any }>('home_sections')?.discovery || { brands: [], skins: [], cards: [] }
    );
    const [recentlySold, setRecentlySold] = useState<any[]>(() => {
        const cachedHome = getAny<{ sections: any[] }>('home_sections');
        const soldSection = cachedHome?.sections?.find((s: any) => s.id === 'recently_sold');
        return (soldSection?.data || []).filter((i: any) => i.type === 'MARKET');
    });
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sellerIds, setSellerIds] = useState<string[]>([]);
    const [priceMin, setPriceMin] = useState<number | null>(null);
    const [priceMax, setPriceMax] = useState<number | null>(null);
    const [transferMethod, setTransferMethod] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState('newest');
    const [priceMeasure, setPriceMeasure] = useState<'current' | 'start'>('current');
    const [offerStatus, setOfferStatus] = useState<string | null>(null);
    const [sellerPanelOpen, setSellerPanelOpen] = useState(false);
    const [pricePanelOpen, setPricePanelOpen] = useState(false);
    const [transferPanelOpen, setTransferPanelOpen] = useState(false);
    const [statusPanelOpen, setStatusPanelOpen] = useState(false);
    const [sortPanelOpen, setSortPanelOpen] = useState(false);

    // ─── Store section tabs (portals-design style text tabs above search) ───
    const [storeSection, setStoreSection] = useState<StoreSection>('all');
    const [offerItems, setOfferItems] = useState<any[]>(() => getAny<any[]>('store_offer_items') || []);
    const [auctionItems, setAuctionItems] = useState<any[]>(() => getAny<any[]>('store_auction_items') || []);

    // ─── Filter/tab option definitions (labels translated at render) ───
    const STORE_FILTERS: NamedFilter[] = [
        { id: 'all', label: tc('all'), icon: <LayoutGrid className="w-3 h-3 text-white/50" /> },
        { id: 'usernames', label: t('usernames'), icon: <AtSign className="w-3 h-3 text-sky-300" /> },
        { id: 'bots', label: 'Bots', icon: <Bot className="w-3 h-3 text-cyan-300" /> },
        { id: 'nfts', label: t('nfts'), icon: <Gem className="w-3 h-3 text-purple-400" /> },
        { id: 'p2p', label: t('p2p'), icon: <Users className="w-3 h-3 text-cyan-300" /> },
        { id: 'platform', label: t('platformOwned'), icon: <Crown className="w-3 h-3 text-yellow-400" /> },
        { id: 'price', label: tc('price'), icon: <Wallet className="w-3 h-3 text-emerald-300" /> },
        { id: 'transfer', label: t('transferMethod'), icon: <Repeat className="w-3 h-3 text-orange-300" /> },
        { id: 'seller', label: t('seller'), icon: <StoreIcon className="w-3 h-3 text-blue-300" /> },
        { id: 'sort', label: t('sortBy'), icon: <ArrowUpDown className="w-3 h-3 text-white/60" /> },
    ];

    const TRANSFER_OPTIONS = [
        { id: 'p2p', label: 'P2P' },
        { id: 'fast', label: 'FAST' },
        { id: 'direct', label: 'DIRECT' },
    ];

    const SORT_OPTIONS = [
        { id: 'newest', label: t('newest') },
        { id: 'price_asc', label: t('priceLowToHigh') },
        { id: 'price_desc', label: t('priceHighToLow') },
    ];

    const AUCTION_SORT_OPTIONS = [
        { id: 'ending', label: t('endingSoonest') },
        { id: 'newest', label: t('newest') },
        { id: 'price_asc', label: t('priceLowToHigh') },
        { id: 'price_desc', label: t('priceHighToLow') },
    ];

    const OFFER_SORT_OPTIONS = [
        { id: 'newest', label: t('newest') },
        { id: 'amount_high', label: t('amountHighToLow') },
        { id: 'amount_low', label: t('amountLowToHigh') },
        { id: 'price_asc', label: t('priceLowToHigh') },
        { id: 'price_desc', label: t('priceHighToLow') },
    ];

    const OFFER_STATUS_OPTIONS = [
        { id: 'pending', label: tc('pending') },
        { id: 'countered', label: t('countered') },
        { id: 'accepted', label: t('accepted') },
        { id: 'rejected', label: t('rejected') },
        { id: 'expired', label: t('expired') },
        { id: 'cancelled', label: tc('cancelled') },
    ];

    const OFFER_FILTERS: NamedFilter[] = [
        { id: 'all', label: tc('all'), icon: <Users className="w-3 h-3 text-white/50" /> },
        { id: 'open', label: t('openToOffers'), icon: <Handshake className="w-3 h-3 text-purple-400" /> },
        { id: 'my', label: t('myOffers'), icon: <UserRound className="w-3 h-3 text-cyan-300" /> },
        { id: 'received', label: t('received'), icon: <Archive className="w-3 h-3 text-emerald-300" /> },
        { id: 'sent', label: t('sent'), icon: <Send className="w-3 h-3 text-blue-300" /> },
        { id: 'accepted', label: t('accepted'), icon: <CheckCircle2 className="w-3 h-3 text-emerald-400" /> },
        { id: 'rejected', label: t('rejected'), icon: <XCircle className="w-3 h-3 text-red-400" /> },
        { id: 'expired', label: t('expired'), icon: <TimerReset className="w-3 h-3 text-amber-300" /> },
        { id: 'usernames', label: t('usernames'), icon: <AtSign className="w-3 h-3 text-sky-300" /> },
        { id: 'nfts', label: t('nfts'), icon: <Gem className="w-3 h-3 text-purple-400" /> },
        { id: 'price', label: tc('price'), icon: <Wallet className="w-3 h-3 text-emerald-300" /> },
        { id: 'seller', label: t('seller'), icon: <StoreIcon className="w-3 h-3 text-blue-300" /> },
        { id: 'status', label: t('offerStatus'), icon: <FilterIcon className="w-3 h-3 text-white/60" /> },
        { id: 'sort', label: t('sortBy'), icon: <ArrowUpDown className="w-3 h-3 text-white/60" /> },
    ];

    const AUCTION_FILTERS: NamedFilter[] = [
        { id: 'all', label: tc('all'), icon: <Users className="w-3 h-3 text-white/50" /> },
        { id: 'live', label: t('live'), icon: <Flame className="w-3 h-3 text-red-400" /> },
        { id: 'upcoming', label: t('upcoming'), icon: <Clock className="w-3 h-3 text-amber-300" /> },
        { id: 'ending_soon', label: t('endingSoon'), icon: <Activity className="w-3 h-3 text-orange-400" /> },
        { id: 'ended', label: t('ended'), icon: <Crown className="w-3 h-3 text-white/40" /> },
        { id: 'usernames', label: t('usernames'), icon: <AtSign className="w-3 h-3 text-sky-300" /> },
        { id: 'nfts', label: t('nfts'), icon: <Gem className="w-3 h-3 text-purple-400" /> },
        { id: 'start_price', label: t('startingPrice'), icon: <Wallet className="w-3 h-3 text-emerald-300" /> },
        { id: 'current_bid', label: t('currentBid'), icon: <Wallet className="w-3 h-3 text-lime-300" /> },
        { id: 'seller', label: t('seller'), icon: <StoreIcon className="w-3 h-3 text-blue-300" /> },
        { id: 'sort', label: t('sortBy'), icon: <ArrowUpDown className="w-3 h-3 text-white/60" /> },
    ];

    const SECTION_TABS: { id: StoreSection; label: string; icon: React.ReactNode }[] = [
        { id: 'all', label: t('allItems'), icon: <LayoutGrid className="w-4 h-4" /> },
        { id: 'store', label: t('store'), icon: <StoreIcon className="w-4 h-4" /> },
        { id: 'offers', label: t('offers'), icon: <Handshake className="w-4 h-4" /> },
        { id: 'auctions', label: t('auctions'), icon: <Gavel className="w-4 h-4" /> },
    ];

    const SECTION_COVERS: Record<StoreSection, {
        title: string; subtitle: string; gradient: string; icon: React.ReactNode; chip: string;
    }> = {
        all: {
            title: t('allItems'), subtitle: t('allMarketSubtitle'), gradient: 'from-blue-500/25 via-indigo-500/10 to-transparent',
            icon: <LayoutGrid className="w-6 h-6 text-sky-300" />, chip: t('liveMarket'),
        },
        store: {
            title: t('store'), subtitle: t('storeCoverSubtitle'), gradient: 'from-emerald-500/25 via-teal-500/10 to-transparent',
            icon: <StoreIcon className="w-6 h-6 text-emerald-300" />, chip: t('chipListed', { count: storeItems.length }),
        },
        offers: {
            title: t('offers'), subtitle: t('offersCoverSubtitle'), gradient: 'from-orange-500/25 via-amber-500/10 to-transparent',
            icon: <Handshake className="w-6 h-6 text-orange-300" />, chip: t('chipOpen', { count: offerItems.length }),
        },
        auctions: {
            title: t('auctions'), subtitle: t('auctionsCoverSubtitle'), gradient: 'from-purple-500/25 via-fuchsia-500/10 to-transparent',
            icon: <Gavel className="w-6 h-6 text-purple-300" />, chip: t('chipLive', { count: auctionItems.length }),
        },
    };

    const loadOffers = useCallback(async () => {
        try {
            const res = await usernameMarketApi.market('offers');
            if (res?.ok) {
                const d = await res.json();
                // Backend returns { success: true, type: 'offers', listings: [...] }
                // Offer items have sale_type='offers' and listing_id=NULL (they live on the asset, not a listing row)
                const itemsList: any[] = Array.isArray(d) ? d : (d?.listings || []);
                const norm = itemsList
                    .filter((l: any) => {
                        const st = String(l.sale_type || l.listing_type || l.type || '').toLowerCase();
                        return st === 'offers' || st === 'offer';
                    })
                    .map((l: any) => {
                        const photo = l.seller_avatar || l.owner_avatar || l.photo_url || l.avatarUrl || l.photoUrl || '/icons/default-avatar.svg';
                        const ownerName = l.seller_name || l.seller_first_name || l.owner_name || (l.username ? `@${l.username}` : '');
                        // Offer assets have no listing_id — use asset_id as unique key
                        const uniqueId = l.listing_id ? `umkt_${l.listing_id}` : `umkt_asset_${l.asset_id}`;
                        const isApp = Boolean(l.is_app || l.ownership_source === 'APP_OWNED' || l.seller_type === 'app' || l.seller_id === 'admin' || !l.seller_telegram_id || l.seller_id === 0 || l.seller_id === '0');
                        return ({
                            ...l,
                            id: uniqueId,
                            listingId: l.listing_id || null,
                            isBot: isBotHandle(l.username),
                            asset_id: l.asset_id,
                            username: l.username,
                            card_name: l.username,
                            listing_type: 'offer',
                            type: 'offer',
                            sale_type: 'offers',
                            is_app: isApp,
                            userKind: isApp ? 'app' : 'p2p',
                            // Offers don't have a fixed price — show 0 or min offer
                            price: Number(l.ask_price || l.min_offer || l.price || 0),
                            seller_id: l.seller_id || l.current_owner_id,
                            seller_name: ownerName,
                            seller_first_name: l.seller_first_name || l.owner_name,
                            avatarUrl: photo,
                            ownerAvatar: photo,
                            seller_avatar: photo,
                            status: l.listing_status || l.pool_status || 'offers',
                        });
                    });
                setOfferItems(norm);
                setCached('store_offer_items', norm);
            }
        } catch (e) { console.error('[store] offers load failed', e); }
    }, []);

    const loadAuctions = useCallback(async () => {
        try {
            const res = await usernameMarketApi.market('auction');
            if (res?.ok) {
                const d = await res.json();
                // Backend: { success: true, type: 'auction', listings: [...] }
                const items: any[] = Array.isArray(d) ? d : (d?.listings || []);
                const norm = items
                    .filter((l: any) => String(l.sale_type || '').toLowerCase() === 'auction')
                    .map((l: any) => {
                        const price = l.current_bid ? parseFloat(l.current_bid) : (l.start_price ? parseFloat(l.start_price) : parseFloat(l.ask_price) || 1);
                        const endDt = l.ends_at ? new Date(l.ends_at).getTime() : null;
                        const photo = l.seller_avatar || l.owner_avatar || l.avatarUrl || l.photo_url || l.photoUrl || '/icons/default-avatar.svg';
                        const ownerLabel = l.seller_name || l.seller_first_name || (l.username ? `@${l.username}` : String(l.seller_id));
                        const isApp = Boolean(l.is_app || l.ownership_source === 'APP_OWNED' || l.seller_type === 'app' || l.seller_id === 'admin' || !l.seller_telegram_id || l.seller_id === 0 || l.seller_id === '0');
                        return ({
                            ...l,
                            id: `umkt_${l.listing_id}`,
                            auctionId: `umkt_${l.listing_id}`,
                            umktId: l.listing_id,
                            isBot: isBotHandle(l.username),
                            target_username: l.username,
                            ownerName: ownerLabel,
                            displayName: l.username ? `@${l.username.replace(/^@/, '')}` : `UMKT_${l.listing_id}`,
                            price,
                            is_app: isApp,
                            userKind: isApp ? 'app' : 'p2p',
                            currentBid: price,
                            start_price: l.start_price ? parseFloat(l.start_price) : price,
                            minBidStep: Number(l.min_increment || 1),
                            bidCount: Number(l.bid_count || 0),
                            isActiveAuction: String(l.listing_status).toLowerCase() === 'active',
                            isNft: l.asset_type === 'nft',
                            assetType: l.asset_type || 'ownership',
                            profileImage: photo,
                            avatarUrl: photo,
                            seller_id: l.seller_id,
                            seller_name: ownerLabel,
                            seller_first_name: l.seller_first_name || l.seller_name,
                            endTime: l.ends_at,
                            starts_at: l.starts_at,
                            ends_at: l.ends_at,
                            type: 'AUCTION',
                        });
                    });
                setAuctionItems(norm);
                setCached('store_auction_items', norm);
            }
        } catch (e) { console.error('[store] auctions load failed', e); }
    }, []);

    const handleSectionChange = useCallback((id: StoreSection) => {
        selectionChanged();
        setStoreSection(id);
        setActiveFilter('all');
        setSellerIds([]);
        setPriceMin(null);
        setPriceMax(null);
        setTransferMethod(null);
        setOfferStatus(null);
        setPriceMeasure('current');
        setSortKey(id === 'auctions' ? 'ending' : 'newest');
        if (id === 'offers') loadOffers();
        if (id === 'auctions') loadAuctions();
        // 'All items' loads all three types
        if (id === 'all') { loadOffers(); loadAuctions(); }
    }, [loadOffers, loadAuctions, selectionChanged]);

    // @ts-ignore
    const tgUser: any = window.Telegram?.WebApp?.initDataUnsafe?.user || {};

    // Filter store items dynamically by quickFind
    const filteredStoreItems = useMemo(() => {
        let out = [...storeItems];
        if (quickFind.trim()) {
            const q = quickFind.toLowerCase().trim();
            out = out.filter(item =>
                (item.title && item.title.toLowerCase().includes(q)) ||
                (item.name && item.name.toLowerCase().includes(q)) ||
                (item.username && item.username.toLowerCase().includes(q)) ||
                (item.serial_number && String(item.serial_number).includes(q)) ||
                (item.card_name && item.card_name.toLowerCase().includes(q))
            );
        }
        if (activeFilter === 'usernames') out = out.filter(i => !i.isNft && String(i.assetType || '').toLowerCase() !== 'nft');
        else if (activeFilter === 'bots') out = out.filter(i => i.isBot);
        else if (activeFilter === 'nfts') out = out.filter(i => i.isNft || String(i.assetType || '').toLowerCase() === 'nft');
        else if (activeFilter === 'p2p') out = out.filter(i => !i.isNft && String(i.assetType || '').toLowerCase() !== 'nft');
        else if (activeFilter === 'platform') out = out.filter(i => !i.seller_id || String(i.seller_id) === '0');
        if (sellerIds.length > 0) out = out.filter(i => sellerIds.includes(String(i.seller_id ?? i.owner_id ?? '')));
        if (transferMethod) out = out.filter(i => String(i.transfer_method || i.transferType || '').toLowerCase() === transferMethod);
        if (priceMin != null) out = out.filter(i => Number(i.price) >= priceMin);
        if (priceMax != null) out = out.filter(i => Number(i.price) <= priceMax);
        if (sortKey === 'price_asc') out.sort((a, b) => Number(a.price) - Number(b.price));
        else if (sortKey === 'price_desc') out.sort((a, b) => Number(b.price) - Number(a.price));
        else out.sort((a, b) => Number(b.listing_id ?? a.listingId ?? 0) - Number(a.listing_id ?? a.listingId ?? 0));
        return out;
    }, [storeItems, quickFind, activeFilter, sellerIds, transferMethod, priceMin, priceMax, sortKey]);

    // Search filter for offers/auctions sections
    const filteredOffers = useMemo(() => {
        let out = [...offerItems];
        if (activeFilter === 'usernames') out = out.filter(i => !i.isNft && String(i.asset_type || i.assetType || '').toLowerCase() !== 'nft');
        else if (activeFilter === 'bots') out = out.filter(i => i.isBot);
        else if (activeFilter === 'nfts') out = out.filter(i => i.isNft || String(i.asset_type || i.assetType || '').toLowerCase() === 'nft');
        else if (activeFilter === 'my' && tgUser?.id) out = out.filter(i => String(i.seller_id ?? i.current_owner_id) === String(tgUser.id));
        else if (['accepted', 'rejected', 'expired', 'pending', 'cancelled'].includes(activeFilter)) {
            out = out.filter(i => String(i.status || i.listing_status || '').toLowerCase() === activeFilter);
        }

        if (offerStatus) {
            out = out.filter(i => String(i.status || i.listing_status || '').toLowerCase() === offerStatus);
        }

        if (sellerIds.length > 0) out = out.filter(i => sellerIds.includes(String(i.seller_id ?? '')));
        if (priceMin != null) out = out.filter(i => Number(i.price) >= priceMin);
        if (priceMax != null) out = out.filter(i => Number(i.price) <= priceMax);

        if (quickFind.trim()) {
            const q = quickFind.toLowerCase().trim();
            out = out.filter(i => (i.username && i.username.toLowerCase().includes(q)) || (i.card_name && String(i.card_name).toLowerCase().includes(q)));
        }

        if (sortKey === 'amount_high' || sortKey === 'price_desc') out.sort((a, b) => Number(b.price ?? 0) - Number(a.price ?? 0));
        else if (sortKey === 'amount_low' || sortKey === 'price_asc') out.sort((a, b) => Number(a.price ?? 0) - Number(b.price ?? 0));
        else out.sort((a, b) => Number(b.listing_id ?? b.id ?? 0) - Number(a.listing_id ?? a.id ?? 0));

        return out;
    }, [offerItems, quickFind, activeFilter, offerStatus, sellerIds, priceMin, priceMax, sortKey, tgUser]);

    const filteredAuctions = useMemo(() => {
        let out = [...auctionItems];

        if (activeFilter === 'usernames') out = out.filter(i => !i.isNft && String(i.assetType || i.asset_type || '').toLowerCase() !== 'nft');
        else if (activeFilter === 'bots') out = out.filter(i => i.isBot);
        else if (activeFilter === 'nfts') out = out.filter(i => i.isNft || String(i.assetType || i.asset_type || '').toLowerCase() === 'nft');
        else if (activeFilter === 'live') out = out.filter(i => i.isActiveAuction !== false && HOURS_LEFT(i) > 0);
        else if (activeFilter === 'upcoming') out = out.filter(i => {
            const s = i.starts_at ? new Date(i.starts_at).getTime() : null;
            return s != null && s > Date.now();
        });
        else if (activeFilter === 'ending_soon') out = out.filter(i => {
            const h = HOURS_LEFT(i);
            return h > 0 && h <= 24;
        });
        else if (activeFilter === 'ended') out = out.filter(i => {
            const h = HOURS_LEFT(i);
            return h <= 0 || i.isActiveAuction === false;
        });

        if (sellerIds.length > 0) out = out.filter(i => sellerIds.includes(String(i.seller_id ?? '')));
        if (priceMin != null || priceMax != null) {
            out = out.filter(i => {
                const p = priceMeasure === 'start'
                    ? Number(i.start_price ?? i.starting_price ?? i.price ?? 0)
                    : Number(i.currentBid ?? i.current_bid ?? i.price ?? 0);
                if (priceMin != null && p < priceMin) return false;
                if (priceMax != null && p > priceMax) return false;
                return true;
            });
        }

        if (quickFind.trim()) {
            const q = quickFind.toLowerCase().trim();
            out = out.filter(i => (i.username && i.username.toLowerCase().includes(q)) || (i.target_username && i.target_username.toLowerCase().includes(q)) || (i.displayName && i.displayName.toLowerCase().includes(q)));
        }

        if (sortKey === 'ending') out.sort((a, b) => HOURS_LEFT(a) - HOURS_LEFT(b));
        else if (sortKey === 'price_asc') out.sort((a, b) => Number(a.price ?? 0) - Number(b.price ?? 0));
        else if (sortKey === 'price_desc') out.sort((a, b) => Number(b.price ?? 0) - Number(a.price ?? 0));
        else if (sortKey === 'newest') out.sort((a, b) => Number(b.listing_id ?? b.umktId ?? 0) - Number(a.listing_id ?? a.umktId ?? 0));

        return out;
    }, [auctionItems, activeFilter, sellerIds, priceMin, priceMax, priceMeasure, sortKey, quickFind]);

    const sellerOptions = useMemo(() => {
        const m = new Map<string, { id: string; label: string }>();
        const targetList = storeSection === 'auctions' ? auctionItems : storeSection === 'offers' ? offerItems : storeItems;
        targetList.forEach((i: any) => {
            const id = String(i.seller_id ?? i.owner_id ?? '');
            if (!id || id === '0') return;
            if (!m.has(id)) m.set(id, { id, label: i.seller_name || i.seller_first_name || `#${id}` });
        });
        return Array.from(m.values());
    }, [storeItems, offerItems, auctionItems, storeSection]);

    const handleFilterSelect = (id: string) => {
        if (id === 'all') {
            setActiveFilter('all');
            setSellerIds([]);
            setPriceMin(null);
            setPriceMax(null);
            setTransferMethod(null);
            setOfferStatus(null);
            setPriceMeasure('current');
            setSortKey(storeSection === 'auctions' ? 'ending' : 'newest');
            return;
        }
        if (id === 'open' || id === 'my') { setActiveFilter(id); return; }
        if (id === 'received' || id === 'sent') { onNavigate('offers_activity'); return; }
        if (id === 'accepted' || id === 'rejected' || id === 'expired' || id === 'pending') {
            setOfferStatus(id);
            setActiveFilter(id);
            return;
        }
        if (id === 'price') { setPriceMeasure('current'); setPricePanelOpen(true); setActiveFilter('all'); return; }
        if (id === 'start_price') { setPriceMeasure('start'); setPricePanelOpen(true); setActiveFilter('all'); return; }
        if (id === 'current_bid') { setPriceMeasure('current'); setPricePanelOpen(true); setActiveFilter('all'); return; }
        if (id === 'transfer') { setTransferPanelOpen(true); setActiveFilter('all'); return; }
        if (id === 'seller') { setSellerPanelOpen(true); setActiveFilter('all'); return; }
        if (id === 'status') { setStatusPanelOpen(true); setActiveFilter('all'); return; }
        if (id === 'sort') { setSortPanelOpen(true); setActiveFilter('all'); return; }
        setActiveFilter(prev => (prev === id ? 'all' : id));
    };

    const myActiveListings = filteredStoreItems.filter((item: any) =>
        (tgUser.id && (item.ownerId === tgUser.id || item.seller_id === tgUser.id || String(item.seller_tg_id) === String(tgUser.id))) ||
        (tgUser.username && item.username && item.username.replace('@', '').trim() === tgUser.username.replace('@', '').trim())
    );

    const handleNavigate = useCallback((page: string, category?: string) => {
        if (isProcessing) return;
        setIsProcessing(true);
        onNavigate(page, category);
        // Reset processing after a short delay or on unmount
        setTimeout(() => setIsProcessing(false), 500);
    }, [onNavigate, isProcessing]);

    const openItem = useCallback((item: any) => {
        if (!item) return;
        const isAuction = item.type === 'AUCTION' || item.isAuction || item.sale_type === 'auction';
        const isOffer = item.type === 'OFFER' || item.type === 'offer' || item.isOffer || item.sale_type === 'offers' || item.sale_type === 'offer';

        if (isAuction) {
            const auctId = String(item.auctionId || item.id || (item.umktId ? `umkt_${item.umktId}` : ''));
            if (auctId) localStorage.setItem('selected_auction_id', auctId);
            handleNavigate('auction_bid', item);
        } else if (isOffer) {
            handleNavigate('offer_view', item);
        } else if (item.type === 'STORE' || item.sale_type === 'store') {
            handleNavigate('brand-item', item);
        } else if (item.type === 'MARKET') {
            handleNavigate('card_view', `list_${item.id}`);
        } else {
            handleNavigate('card_view', item);
        }
    }, [handleNavigate]);

    const loadV2Store = useCallback(async () => {
        try {
            const umktRes = await usernameMarketApi.market('store');

            const combined: any[] = [];

            // Unified store listings from username_market (single source of truth)
            if (umktRes?.ok) {
                try {
                    const r = await umktRes.json();
                    // Backend: { success: true, type: 'store', listings: [...] }
                    const rawListings: any[] = Array.isArray(r) ? r : (r?.listings || []);
                    if (rawListings.length > 0 || r.success) {
                        const items = rawListings
                            .filter((l: any) => l.sale_type === 'store' && ['active', 'listed', 'for_sale'].includes(String(l.listing_status || '').toLowerCase()))
                            .map((l: any) => {
                                const photo = l.seller_avatar || l.owner_avatar || l.avatarUrl || l.photo_url || l.photoUrl || '/icons/default-avatar.svg';
                                const ownerLabel = l.seller_name || l.seller_first_name || (l.username ? `@${l.username.replace(/^@/, '')}` : undefined);
                                const isApp = Boolean(l.is_app || l.ownership_source === 'APP_OWNED' || l.seller_type === 'app' || l.seller_id === 'admin' || !l.seller_telegram_id || l.seller_id === 0 || l.seller_id === '0');
                                return ({
                                    id: `umkt_${l.listing_id}`,
                                    listingId: l.listing_id,
                                    type: 'STORE',
                                    isBot: isBotHandle(l.username),
                                    price: Number(l.ask_price || l.price),
                                    username: l.username || undefined,
                                    name: l.username ? `@${l.username.replace(/^@/, '')}` : `UMKT_${l.listing_id}`,
                                    card_name: l.username || undefined,
                                    target_username: l.username || undefined,
                                    verified_username: l.username || undefined,
                                    assetType: l.asset_type || 'ownership',
                                    isNft: l.asset_type === 'nft',
                                    is_app: isApp,
                                    userKind: isApp ? 'app' : 'p2p',
                                    asset_id: l.asset_id,
                                    seller_id: l.seller_id,
                                    seller_telegram_id: l.seller_telegram_id || null,
                                    owner_id: l.current_owner_id,
                                    seller_first_name: l.seller_first_name || l.seller_name,
                                    seller_last_name: l.seller_last_name || '',
                                    seller_name: ownerLabel,
                                    owner_name: ownerLabel,
                                    seller_username: l.seller_username || l.username,
                                    avatarUrl: photo,
                                    ownerAvatar: photo,
                                    ownerImage: photo,
                                    seller_avatar: photo,
                                    owner_avatar: photo,
                                    serial: l.listing_id,
                                    qty: 1,
                                    status: l.listing_status,
                                    source: 'umkt_store'
                                });
                            });
                        combined.push(...items);
                    }
                } catch (e) { console.error('[store] v2 parse error', e); }
            }

            // Official v2 store listings (listings + store_listings) — kept in sync
            try {
                const v2Res = await v2Api.listings({ type: 'store' });
                if (v2Res?.success && Array.isArray(v2Res.listings)) {
                    const activeStates = ['active', 'listed', 'for_sale'];
                    v2Res.listings
                        .filter((l: any) => String(l.listing_type || '').toLowerCase() === 'store' && activeStates.includes(String(l.status || '').toLowerCase()))
                        .forEach((l: any) => {
                            const photo = l.seller_avatar || l.avatarUrl || l.photo_url || '/icons/default-avatar.svg';
                            const ownerLabel = l.seller_first_name || l.seller_name || (l.username ? `@${l.username.replace(/^@/, '')}` : `Listing #${l.id}`);
                            combined.push({
                                id: `v2_${l.id}`,
                                listingId: l.id,
                                type: 'STORE',
                                isBot: isBotHandle(l.username),
                                price: Number(l.sale_price ?? l.price ?? 0),
                                username: l.username || undefined,
                                name: l.username ? `@${l.username.replace(/^@/, '')}` : (l.nft_id ? `NFT #${l.nft_id}` : `ITEM #${l.id}`),
                                card_name: l.username || undefined,
                                target_username: l.username || undefined,
                                verified_username: l.username || undefined,
                                assetType: l.asset_type || (l.nft_id ? 'nft' : 'username'),
                                isNft: l.asset_type === 'nft' || Boolean(l.nft_id),
                                is_app: l.ownership_source === 'APP_OWNED' || !l.seller_id || l.seller_id === 0,
                                userKind: l.ownership_source === 'APP_OWNED' ? 'app' : 'p2p',
                                asset_id: l.asset_id,
                                seller_id: l.seller_id,
                                seller_telegram_id: l.seller_telegram_id || null,
                                owner_id: l.seller_id,
                                seller_first_name: l.seller_first_name || l.seller_name,
                                seller_last_name: l.seller_last_name || '',
                                seller_name: ownerLabel,
                                owner_name: ownerLabel,
                                seller_username: l.seller_username || l.username,
                                avatarUrl: photo,
                                ownerAvatar: photo,
                                ownerImage: photo,
                                seller_avatar: photo,
                                owner_avatar: photo,
                                serial: l.id,
                                qty: 1,
                                status: l.status,
                                source: 'v2_store'
                            });
                        });
                }
            } catch (e) { console.error('[store] v2 listings merge error', e); }

            // De-duplicate by username or ID & strictly keep STORE items
            const seen = new Set();
            const uniqueItems = combined.filter((item: any) => {
                if (resolveListingCategory(item) !== 'store') return false;
                const cleanName = String(item.username || item.target_username || item.displayName || item.name || '').toLowerCase().replace(/^@/, '').trim();
                const key = cleanName && !cleanName.includes('item_') && !cleanName.includes('card') ? `u:${cleanName}` : `id:${item.id || item.listingId}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

            setStoreItems(uniqueItems);
            setCached('store_listings', uniqueItems);
        } catch (e) {
            console.error('[store] load failed', e);
        }
    }, []);

    useEffect(() => {
        if (isActive) {
            loadV2Store();
            // Load every type up-front so the "All items" tab is complete
            // without having to open the Offers/Auctions tabs first.
            loadOffers();
            loadAuctions();
        }

        // ─── Live Pulse: Always fetch ───
        const fetchActiveUsers = () => {
            userApi.getActive().then(r => r.json()).then(data => {
                if (data.success) {
                    setActiveUsers(data.users || []);
                    setTotalActive(data.totalActive || 0);
                    setCached('active_users', { users: data.users, totalActive: data.totalActive });
                }
            }).catch(console.error);
        };
        fetchActiveUsers();
    }, [isActive, loadV2Store, loadOffers, loadAuctions]);

    // Real-time socket updates for Store
    useEffect(() => {
        socket.on('push_usernames_updated', loadV2Store);
        socket.on('market_update', loadV2Store);
        socket.on('username_listed', loadV2Store);
        socket.on('username_market_update', loadV2Store);

        return () => {
            socket.off('push_usernames_updated', loadV2Store);
            socket.off('market_update', loadV2Store);
            socket.off('username_listed', loadV2Store);
            socket.off('username_market_update', loadV2Store);
        };
    }, [loadV2Store]);


    const isListView = viewMode === 'list' || viewMode === 'compact';

    // 'All items' grid — merges all three types, deduped
    const allItemsMerged = useMemo(() => {
        const seen = new Set<string>();
        const out: any[] = [];
        const push = (arr: any[]) => arr.forEach(it => {
            const key = String(it.id ?? it.auctionId ?? it.auction_id);
            if (key && !seen.has(key)) { seen.add(key); out.push(it); }
        });
        push(filteredStoreItems);
        push(filteredOffers);
        push(filteredAuctions);
        return out;
    }, [filteredStoreItems, filteredOffers, filteredAuctions]);

    // List mode groups — deduped so nothing repeats
    const listSold = useMemo(() => {
        const seen = new Set<string>();
        return recentlySold.filter(it => {
            const k = String(it.id ?? it.auctionId ?? it.auction_id);
            if (!k || seen.has(k)) return false;
            seen.add(k);
            return true;
        });
    }, [recentlySold]);

    // List-mode: all items merged (for 'all' tab)
    const listOther = useMemo(() => {
        const seen = new Set<string>();
        const out: any[] = [];
        [...filteredStoreItems, ...filteredOffers, ...filteredAuctions].forEach(it => {
            const k = String(it.id ?? it.auctionId ?? it.auction_id);
            if (k && !seen.has(k)) { seen.add(k); out.push(it); }
        });
        return out;
    }, [filteredStoreItems, filteredOffers, filteredAuctions]);

    return (
        <div className="pt-2 min-h-screen pb-24">
            <div className="px-[9px] sm:px-[15px] mb-[7px]">
                <BalanceBar
                    onDepositClick={() => onNavigate('wallet', 'deposit')}
                    onHistoryClick={() => onNavigate('p2p_history')}
                    logoVariant="lottie"
                    logoActive={isActive}
                    logoIcon={<ShoppingBag size={20} className="text-sky-300" />}
                />
            </div>

            {/* ─── Category text tabs above search (portals-design style) ─── */}
            <div className="mb-[7px] px-[9px] sm:px-[15px] overlay-scroll no-scrollbar">
                <div className="flex items-center gap-[13px] overflow-x-auto no-scrollbar">
                    {SECTION_TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => handleSectionChange(t.id)}
                            className={`text-[15px] font-bold tracking-tight cursor-pointer transition-colors duration-200 shrink-0 flex items-center gap-1 ${
                                storeSection === t.id ? 'text-white' : 'text-white/35 hover:text-white/60'
                            }`}
                        >
                            {t.icon}
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ─── Section cover (changes per tab) ─── */}
            <div className="px-[9px] sm:px-[15px] mb-[7px]">
                <div className={`relative overflow-hidden rounded-[19px] border border-white/[0.06] bg-gradient-to-r ${SECTION_COVERS[storeSection].gradient} px-[13px] py-[11px]`}>
                    <BannerMediaLayer bgKey={`bg_banner_store_${storeSection}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10" />
                    <div className="relative flex items-center gap-[9px]">
                        <div className="w-9 h-9 shrink-0 rounded-[14px] bg-black/30 backdrop-blur-md flex items-center justify-center border border-white/[0.07]">
                            {SECTION_COVERS[storeSection].icon}
                        </div>
                        <div className="min-w-0">
                            <p className="text-white text-[13px] font-bold tracking-tight leading-tight">
                                {SECTION_COVERS[storeSection].title}
                            </p>
                            <p className="text-white/50 text-[10px] font-medium mt-0.5 leading-snug line-clamp-1">
                                {SECTION_COVERS[storeSection].subtitle}
                            </p>
                        </div>
                        <span className="ml-auto shrink-0 rounded-full glass-chip px-[9px] py-[4px] text-[10px] font-bold text-white">
                            {SECTION_COVERS[storeSection].chip}
                        </span>
                    </div>

                    {/* Admin-only: upload / remove this section's banner image or video */}
                    <BannerMediaControls bgKey={`bg_banner_store_${storeSection}`} className="-top-2 -right-2" />
                </div>
            </div>

            {/* EXPERIMENTAL FILTER & SEARCH BAR SECTION */}
            <ExperimentalFilterBar
                quickFind={quickFind}
                onQuickFindChange={(val) => setQuickFind(val)}
                onViewModeChange={setViewMode}
                viewMode={viewMode}
                items={storeSection === 'all' ? allItemsMerged : storeSection === 'store' ? storeItems : storeSection === 'offers' ? offerItems : auctionItems}
                filters={storeSection === 'all' ? STORE_FILTERS : storeSection === 'store' ? STORE_FILTERS : storeSection === 'offers' ? OFFER_FILTERS : AUCTION_FILTERS}
                activeFilter={activeFilter}
                onFilterSelect={handleFilterSelect}
                onFilterApply={(ids) => onNavigate('all_items', 'Filtered')}
                onPurchasesClick={() => onNavigate('purchases')}
            />

            <FilterModal 
                isOpen={isFilterOpen} 
                onClose={() => setIsFilterOpen(false)} 
                onApply={(filters) => {
                    console.log('Applied filters:', filters);
                    onNavigate('all_items', filters.category || 'Filtered');
                }}
            />

            {/* ─── Section-specific content ─── */}
            {storeSection === 'offers' && filteredOffers.length > 0 && (
                isListView ? (
                    <SectionListView
                        title=""
                        icon={null}
                        items={filteredOffers}
                        sectionId="offers_tab"
                        forceOffer
                        onItemClick={(item) => handleNavigate('offer_view', item)}
                    />
                ) : (
                    <div className="px-[9px] sm:px-[15px] grid grid-cols-2 sm:grid-cols-3 gap-[7px]">
                        {filteredOffers.map((item: any) => (
                            <CardRenderer
                                key={`offer-grid-${item.id}`}
                                {...item}
                                context="offer"
                                isOffer
                                itemType="offer"
                                variant="premium"
                                scale={0.55}
                                compact
                                showCardIcon={true}
                                compactOwner={true}
                                onBuy={() => handleNavigate('offer_view', item)}
                                onInteract={() => handleNavigate('offer_view', item)}
                                onClick={() => handleNavigate('offer_view', item)}
                            />
                        ))}
                    </div>
                )
            )}

            {storeSection === 'auctions' && filteredAuctions.length > 0 && (
                isListView ? (
                    <SectionListView
                        title=""
                        icon={null}
                        items={filteredAuctions}
                        sectionId="auctions_tab"
                        forceAuction
                        onItemClick={(item) => {
                            localStorage.setItem('selected_auction_id', String(item.umktId ?? item.listing_id));
                            handleNavigate('auction_bid', String(item.umktId ?? item.listing_id));
                        }}
                    />
                ) : (
                    <div className="px-[9px] sm:px-[15px] grid grid-cols-2 sm:grid-cols-3 gap-[7px]">
                        {filteredAuctions.map((item: any) => (
                            <CardRenderer
                                key={`auction-grid-${item.id}`}
                                {...item}
                                context="auction"
                                forceAuction
                                variant="premium"
                                scale={0.55}
                                compact
                                showCardIcon={true}
                                compactOwner={true}
                                hideBuyButton
                                onBuy={() => {
                                    localStorage.setItem('selected_auction_id', String(item.umktId ?? item.listing_id));
                                    handleNavigate('auction_bid', String(item.umktId ?? item.listing_id));
                                }}
                                onClick={() => {
                                    localStorage.setItem('selected_auction_id', String(item.umktId ?? item.listing_id));
                                    handleNavigate('auction_bid', String(item.umktId ?? item.listing_id));
                                }}
                            />
                        ))}
                    </div>
                )
            )}

            {storeSection === 'store' && filteredStoreItems.length > 0 && (
                isListView ? (
                    <SectionListView
                        title=""
                        icon={null}
                        items={filteredStoreItems}
                        sectionId="store_tab"
                        onItemClick={openItem}
                    />
                ) : (
                    <div className="px-[9px] sm:px-[15px] grid grid-cols-2 sm:grid-cols-3 gap-[7px]">
                        {filteredStoreItems.map((item: any) => (
                            <CardRenderer
                                key={`store-section-${item.id}`}
                                {...item}
                                variant="premium"
                                scale={0.55}
                                compact
                                showCardIcon={true}
                                compactOwner={true}
                                onBuy={() => openItem(item)}
                                onInteract={() => openItem(item)}
                                onClick={() => openItem(item)}
                            />
                        ))}
                    </div>
                )
            )}

            {storeSection !== 'all' && storeSection !== 'offers' && storeSection !== 'auctions' && storeSection !== 'store' && null}

            {/* 'All items' — shows all three types merged (list mode) */}
            {storeSection === 'all' && listOther.length > 0 && isListView && (
                <SectionListView
                    title=""
                    icon={null}
                    items={listOther}
                    sectionId="store_all"
                    onItemClick={(item) => {
                        if (item.type === 'AUCTION') {
                            localStorage.setItem('selected_auction_id', String(item.umktId ?? item.id));
                            handleNavigate('auction_bid', String(item.umktId ?? item.id));
                        } else if (item.type === 'offer' || item.sale_type === 'offers') {
                            handleNavigate('offer_view', item);
                        } else {
                            openItem(item);
                        }
                    }}
                />
            )}

            {/* 'All items' — shows all three types merged (grid mode) */}
            {storeSection === 'all' && allItemsMerged.length > 0 ? (
                isListView ? null : (
                    <div className="px-[9px] sm:px-[15px] grid grid-cols-2 sm:grid-cols-3 gap-[7px]">
                        {allItemsMerged.map((item: any) => {
                            const key = String(item.id ?? item.auctionId ?? item.auction_id);
                            const isAuction = item.type === 'AUCTION';
                            const isOfferItem = item.type === 'offer' || item.sale_type === 'offers';
                            return (
                                <CardRenderer
                                    key={`all-grid-${key}`}
                                    {...item}
                                    variant="premium"
                                    scale={0.55}
                                    compact
                                    showCardIcon={true}
                                    compactOwner={true}
                                    context={isAuction ? 'auction' : undefined}
                                    forceAuction={isAuction}
                                    onBuy={() => {
                                        if (isAuction) {
                                            localStorage.setItem('selected_auction_id', String(item.umktId ?? item.id));
                                            handleNavigate('auction_bid', String(item.umktId ?? item.id));
                                        } else {
                                            openItem(item);
                                        }
                                    }}
                                    onClick={() => {
                                        if (isAuction) {
                                            localStorage.setItem('selected_auction_id', String(item.umktId ?? item.id));
                                            handleNavigate('auction_bid', String(item.umktId ?? item.id));
                                        } else {
                                            openItem(item);
                                        }
                                    }}
                                />
                            );
                        })}
                    </div>
                )
            ) : null}

            {/* Dynamic Empty State */}
            {((storeSection === 'offers' && filteredOffers.length === 0) ||
              (storeSection === 'auctions' && filteredAuctions.length === 0) ||
              (storeSection === 'store' && filteredStoreItems.length === 0) ||
              (storeSection === 'all' && allItemsMerged.length === 0)) && (() => {
                const meta = (
                    storeSection === 'offers' || activeFilter === 'offers' ? { emojiId: '107', title: t('noOffersTitle'), desc: t('noOffersDesc') } :
                    storeSection === 'auctions' || activeFilter === 'auctions' || activeFilter === 'live' ? { emojiId: '107', title: t('noAuctionsTitle'), desc: t('noAuctionsDesc') } :
                    storeSection === 'store' || activeFilter === 'store' ? { emojiId: '105', title: t('noStoreListingsTitle'), desc: t('noStoreListingsDesc') } :
                    activeFilter === 'usernames' ? { emojiId: '111', title: t('noUsernamesTitle'), desc: t('noUsernamesDesc') } :
                    activeFilter === 'nfts' ? { emojiId: '98', title: t('noNftsTitle'), desc: t('noNftsDesc') } :
                    activeFilter === 'p2p' ? { emojiId: '91', title: t('noP2pTitle'), desc: t('noP2pDesc') } :
                    activeFilter === 'platform' || activeFilter === 'seller' ? { emojiId: '87', title: t('noPlatformTitle'), desc: t('noPlatformDesc') } :
                    activeFilter === 'price' ? { emojiId: '101', title: t('noPriceRangeTitle'), desc: t('noPriceRangeDesc') } :
                    activeFilter === 'transfer' ? { emojiId: '108', title: t('noTransfersTitle'), desc: t('noTransfersDesc') } :
                    activeFilter === 'sort' ? { emojiId: '110', title: t('noSortTitle'), desc: t('noSortDesc') } :
                    { emojiId: 'ch1', title: t('noItemsTitle'), desc: t('noItemsDesc') }
                );

                return (
                    <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                        <div className="w-24 h-24 mb-3 flex items-center justify-center">
                            <CustomEmojiPlayer emojiId={meta.emojiId} size={92} />
                        </div>
                        <p className="text-white font-extrabold text-base mb-1">{meta.title}</p>
                        <p className="text-[12px] text-white/40 max-w-[250px] mb-4">{meta.desc}</p>
                        {activeFilter !== 'all' && (
                            <button
                                onClick={() => handleFilterSelect('all')}
                                className="mt-2 px-6 py-2.5 rounded-full bg-white text-black font-extrabold text-xs hover:bg-white/90 active:scale-95 transition-all shadow-md"
                            >
                                {t('clearFilters')}
                            </button>
                        )}
                    </div>
                );
            })()}

            <FilterPanel
                open={sellerPanelOpen}
                title={t('filterBySeller')}
                icon={<StoreIcon className="w-4 h-4 text-blue-400" />}
                options={sellerOptions}
                selected={sellerIds}
                searchable
                onToggle={(id) => setSellerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                onClose={() => setSellerPanelOpen(false)}
                onClear={() => setSellerIds([])}
            />

            <PricePanel
                open={pricePanelOpen}
                title={`${t('filterBy')} ${storeSection === 'auctions' ? (priceMeasure === 'start' ? t('startingPrice') : t('currentBid')) : tc('price')} (GRAM)`}
                min={priceMin}
                max={priceMax}
                onMinChange={setPriceMin}
                onMaxChange={setPriceMax}
                onClose={() => setPricePanelOpen(false)}
                onClear={() => { setPriceMin(null); setPriceMax(null); }}
                onApply={() => {}}
            />

            <FilterPanel
                open={transferPanelOpen}
                title={`${t('filterBy')} ${t('transferMethod')}`}
                icon={<Repeat className="w-4 h-4 text-orange-300" />}
                options={TRANSFER_OPTIONS}
                selected={transferMethod ? [transferMethod] : []}
                singleSelect
                onToggle={(id) => setTransferMethod(id || null)}
                onClose={() => setTransferPanelOpen(false)}
                onClear={() => setTransferMethod(null)}
            />

            <FilterPanel
                open={statusPanelOpen}
                title={t('offerStatus')}
                icon={<FilterIcon className="w-4 h-4 text-purple-300" />}
                options={OFFER_STATUS_OPTIONS}
                selected={offerStatus ? [offerStatus] : []}
                singleSelect
                onToggle={(id) => setOfferStatus(id || null)}
                onClose={() => setStatusPanelOpen(false)}
                onClear={() => setOfferStatus(null)}
            />

            <FilterPanel
                open={sortPanelOpen}
                title={t('sortBy')}
                icon={<ArrowUpDown className="w-4 h-4 text-white/60" />}
                options={storeSection === 'auctions' ? AUCTION_SORT_OPTIONS : storeSection === 'offers' ? OFFER_SORT_OPTIONS : SORT_OPTIONS}
                selected={[sortKey]}
                singleSelect
                onToggle={(id) => setSortKey(id || (storeSection === 'auctions' ? 'ending' : 'newest'))}
                onClose={() => setSortPanelOpen(false)}
                onClear={() => setSortKey(storeSection === 'auctions' ? 'ending' : 'newest')}
            />

        </div>
    );
};


export default Store;
